<template>
  <Dialog :commonDialog="documentDialog" :dialog-width="800">
    <template v-slot:title>Update Employee OT</template>
    <template v-slot:body>
      <v-form ref="otForm" v-model="formValid">
        <v-container>
          <v-row>
            <!-- Date Picker -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Start Date</label>
              <DatePicker
                v-model="formData.ot_start"
                :rules="[validateRules.required(formData.ot_start, 'Start Date')]"
                placeholder="Start Date"
                @change="onStartChange"
                @input="onStartChange"
              ></DatePicker>
            </v-col>
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Start Time</label>

              <TimePicker
                key="ot-start-time"
                id="ot-start-time"
                placeholder="Start Time"
                v-model="formData.ot_start_time"
                type="start-time"
                @change="onStartChange"
                @input="onStartChange"
              />
            </v-col>

            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">End Date</label>
              <DatePicker
                v-model="formData.ot_end"
                :rules="[validateRules.required(formData.ot_end, 'End Date')]"
                placeholder="End Date"
                @change="onEndChange"
                @input="onEndChange"
              ></DatePicker>
            </v-col>
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">End Time</label>

              <TimePicker
                key="ot-end-time"
                id="ot-end-time"
                placeholder="End Time"
                v-model="formData.ot_end_time"
                type="end-time"
                @change="onEndChange"
                @input="onEndChange"
              />
            </v-col>

            <!-- Duration -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">Duration (HH:MM)</label>
              <v-text-field
                v-model="formData.total_time"
                :rules="durationRules"
                label="Duration (HH:MM)"
                placeholder="HH:MM (e.g. 05:30)"
                dense
                filled
                solo
                flat
                @input="onDurationInput"
                @change="onDurationInput"
                @keyup="onDurationInput"
              ></v-text-field>
            </v-col>

            <!-- OT Rate -->
            <v-col cols="6">
              <label class="font-weight-600 font-size-18 required">OT Rate</label>
              <v-select
                :items="rateList"
                v-model="formData.rate"
                :rules="[validateRules.required(formData.rate, 'OT Rate')]"
                item-text="text"
                item-value="value"
                placeholder="Select OT Rate"
                dense
                filled
                solo
                flat
                color="cyan"
                item-color="cyan"
              ></v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </template>
    <template v-slot:action>
      <v-btn
        depressed
        color="cyan"
        class="white--text"
        :loading="formLoading"
        :disabled="!formValid || formLoading"
        @click="submitForm"
      >
        Save
      </v-btn>
      <v-btn depressed class="mx-2" :disabled="formLoading" @click="cancel()">
        Cancel
      </v-btn>
    </template>
  </Dialog>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog";
import DatePicker from "@/view/components/QDatePicker";
import ValidationMixin from "@/core/plugins/validation-mixin";
import CommonMixin from "@/core/plugins/common-mixin";
import TimePicker from "@/view/components/main-job/TimePicker.vue";
import { PUT } from "@/core/services/store/request.module";
import moment from "moment-timezone";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);
export default {
  name: "UpdateOt",
  mixins: [ValidationMixin, CommonMixin],
  components: {
    Dialog,
    DatePicker,
    TimePicker,
  },
  props: {
    typeId: {
      type: Number,
      default: 0,
    },
    otRow: {
      type: Object,
      default: () => {
        return {};
      },
    },
    documentDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      formLoading: false,
      rateList: [
        { text: "1.5 OT", value: 1.5 },
        { text: "2.0 OT", value: 2.0 },
      ],
      formData: {
        id: null,
        ot_start: null,
        ot_start_time: null,
        ot_end: null,
        ot_end_time: null,
        total_time: null,
        rate: 1.5,
      },
    };
  },
  computed: {
    durationRules() {
      return [
        (v) => !!v || "Duration is required",
        (v) =>
          /^([0-9]{1,2}):([0-5][0-9])$/.test(String(v || "").trim()) ||
          "Duration must be in HH:MM format (e.g. 05:30)",
      ];
    },
  },
  watch: {
    documentDialog: {
      handler(param) {
        if (param) {
          this.formData.id = this.otRow?.id ? this.lodash.toSafeInteger(this.otRow?.id) : null;
          this.formData.ot_start = this.otRow?.ot_start ? moment(this.otRow?.ot_start).format("YYYY-MM-DD") : null;
          this.formData.ot_start_time = this.otRow?.ot_start ? moment(this.otRow?.ot_start).format("hh:mm A") : null;
          if (this.otRow?.ot_end) {
            const endM = moment(this.otRow.ot_end);
            this.formData.ot_end = endM.format("YYYY-MM-DD");
            this.formData.ot_end_time = (endM.format("HH:mm:ss") === "23:59:59" || endM.format("HH:mm:ss") === "23:59:00" || endM.format("HH:mm") === "23:59")
              ? "12:00 AM"
              : endM.format("hh:mm A");
          } else {
            this.formData.ot_end = null;
            this.formData.ot_end_time = null;
          }
          this.formData.total_time = this.otRow?.total_time ?? null;
          this.formData.rate = this.otRow?.rate ? parseFloat(this.otRow?.rate) : 1.5;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getStartMoment() {
      if (!this.formData.ot_start || !this.formData.ot_start_time) {
        return null;
      }
      const dateStr = String(this.formData.ot_start).trim();
      const timeStr = String(this.formData.ot_start_time).trim();
      const m = moment(`${dateStr} ${timeStr}`, [
        "YYYY-MM-DD hh:mm A",
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD h:mma",
        "YYYY-MM-DD hh:mma",
        "DD-MM-YYYY hh:mm A",
        "DD/MM/YYYY hh:mm A",
        "YYYY-MM-DD",
      ]);
      return m.isValid() ? m : null;
    },
    getEndMoment() {
      if (!this.formData.ot_end || !this.formData.ot_end_time) {
        return null;
      }
      const dateStr = String(this.formData.ot_end).trim();
      const timeStr = String(this.formData.ot_end_time).trim();
      if (timeStr === "12:00 AM" || timeStr === "12:00 am") {
        return moment(`${dateStr} 23:59:59`, "YYYY-MM-DD HH:mm:ss");
      }
      const m = moment(`${dateStr} ${timeStr}`, [
        "YYYY-MM-DD hh:mm A",
        "YYYY-MM-DD h:mm A",
        "YYYY-MM-DD HH:mm",
        "YYYY-MM-DD h:mma",
        "YYYY-MM-DD hh:mma",
        "DD-MM-YYYY hh:mm A",
        "DD/MM/YYYY hh:mm A",
        "YYYY-MM-DD",
      ]);
      return m.isValid() ? m : null;
    },
    onStartChange() {
      this.$nextTick(() => {
        if (this.formData.ot_end && this.formData.ot_end_time) {
          this.calculateDurationFromEnd();
        } else if (this.formData.total_time) {
          this.calculateEndTimeFromDuration();
        }
      });
    },
    onEndChange() {
      this.$nextTick(() => {
        this.calculateDurationFromEnd();
      });
    },
    calculateDurationFromEnd() {
      const start = this.getStartMoment();
      const end = this.getEndMoment();
      if (start && end && end.isSameOrAfter(start)) {
        if (end.format("HH:mm") === "23:59") {
          end.second(59);
        }
        const diffMinutes = Math.round(end.diff(start, "seconds") / 60);
        const hours = String(Math.floor(diffMinutes / 60)).padStart(2, "0");
        const mins = String(diffMinutes % 60).padStart(2, "0");
        this.formData.total_time = `${hours}:${mins}`;
      }
    },
    onDurationInput() {
      this.calculateEndTimeFromDuration();
    },
    calculateEndTimeFromDuration() {
      const val = String(this.formData.total_time || "").trim();
      if (!val) {
        return;
      }
      const match = val.match(/^([0-9]{1,2}):([0-5][0-9])$/);
      if (!match) {
        return;
      }
      const h = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      const minutes = h * 60 + m;

      const start = this.getStartMoment();
      if (start && minutes >= 0) {
        const end = start.clone().add(minutes, "minutes");
        this.formData.ot_end = end.format("YYYY-MM-DD");
        this.formData.ot_end_time = end.format("hh:mm A");
      }
    },
    resetForm() {
      this.formData = {
        id: null,
        ot_start: null,
        ot_start_time: null,
        ot_end: null,
        ot_end_time: null,
        total_time: null,
        rate: 1.5,
      };
      if (this.$refs.otForm) {
        this.$refs.otForm.resetValidation();
      }
    },
    submitForm() {
      const _this = this;
      if (!_this.$refs.otForm.validate()) {
        return false;
      }
      _this.formLoading = true;
      _this.$store
        .dispatch(PUT, {
          url: `engineer/ot-record/${_this.formData.id}`,
          data: _this.formData,
        })
        .then(() => {
          _this.$emit("success");
          _this.cancel();
        })
        .catch((error) => {
          _this.logError(error);
        })
        .finally(() => {
          _this.formLoading = false;
        });
    },
    cancel() {
      this.resetForm();
      this.$emit("close");
    },
  },
  mounted() { },
};
</script>
